import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Link2, Unlink, CheckCircle2, Star, Plus } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Profile() {
  const { user } = useAuth();
  const [status, setStatus] = useState({ connected: false, email: null, mailboxes: [] });
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();

  const loadStatus = () => api.get("/oauth/gmail/status").then((r) => setStatus(r.data)).finally(() => setLoading(false));

  useEffect(() => {
    loadStatus();
    const g = params.get("gmail");
    if (g === "connected") { toast.success("Account Google collegato"); params.delete("gmail"); setParams(params, { replace: true }); }
    else if (g === "error") { toast.error("Collegamento Google non riuscito"); params.delete("gmail"); setParams(params, { replace: true }); }
  }, []); // eslint-disable-line

  const connect = async () => {
    try {
      const { data } = await api.get("/oauth/gmail/login", { params: { origin: window.location.origin } });
      window.location.href = data.url;
    } catch (e) { toast.error("Impossibile avviare il collegamento Google"); }
  };

  const disconnect = async (mb) => {
    if (!window.confirm(`Scollegare ${mb.email}?`)) return;
    try {
      await api.delete(`/oauth/gmail/mailboxes/${mb.id}`);
      toast.success("Casella scollegata");
      loadStatus();
    } catch (e) { toast.error("Errore durante lo scollegamento"); }
  };

  const setPrimary = async (mb) => {
    try {
      await api.post(`/oauth/gmail/mailboxes/${mb.id}/primary`);
      loadStatus();
    } catch (e) { toast.error("Errore"); }
  };

  return (
    <div className="max-w-[720px] flex flex-col gap-6" data-testid="profile-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Profilo</h1>
        <p className="text-xs text-subtle mt-0.5">{user?.name} · {user?.email}</p>
      </div>

      <div className="bg-white border border-line rounded-[8px] p-5" data-testid="gmail-card">
        <div className="flex items-center gap-2 mb-1">
          <Mail className="w-4 h-4 text-brand-dark" strokeWidth={1.5} />
          <h2 className="text-base font-semibold text-ink">Caselle email (Google)</h2>
        </div>
        <p className="text-sm text-subtle mb-4">
          Collega uno o più account Google. Le email a te assegnate partiranno da queste caselle (a rotazione, per aumentare i volumi).
          La casella <b>primaria</b> è usata per le bozze manuali.
        </p>

        {loading ? (
          <p className="text-sm text-subtle">Caricamento…</p>
        ) : (
          <div className="flex flex-col gap-2">
            {status.mailboxes.map((mb) => (
              <div key={mb.id} data-testid={`mailbox-${mb.id}`} className="flex items-center justify-between gap-3 bg-brand-soft/40 border border-brand-soft rounded-[6px] px-4 py-3">
                <div className="flex items-center gap-2 min-w-0">
                  <CheckCircle2 className="w-4 h-4 text-brand-dark shrink-0" strokeWidth={1.5} />
                  <span className="text-sm font-medium text-ink truncate">{mb.email}</span>
                  {mb.is_primary && <span className="text-[10px] bg-brand-dark text-white rounded-full px-2 py-0.5 shrink-0">Primaria</span>}
                  {mb.paused && <span className="text-[10px] bg-destructive/10 text-destructive rounded-full px-2 py-0.5 shrink-0">In pausa</span>}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {!mb.is_primary && (
                    <button data-testid={`mailbox-primary-${mb.id}`} onClick={() => setPrimary(mb)} className="flex items-center gap-1 text-xs text-subtle hover:text-brand-dark transition-colors">
                      <Star className="w-3.5 h-3.5" strokeWidth={1.5} /> Rendi primaria
                    </button>
                  )}
                  <button data-testid={`mailbox-disconnect-${mb.id}`} onClick={() => disconnect(mb)} className="flex items-center gap-1 text-xs text-subtle hover:text-destructive transition-colors">
                    <Unlink className="w-3.5 h-3.5" strokeWidth={1.5} /> Scollega
                  </button>
                </div>
              </div>
            ))}
            <button data-testid="gmail-connect" onClick={connect} className="flex items-center gap-2 self-start mt-1 bg-brand text-ink font-medium rounded-[6px] px-4 py-2.5 text-sm hover:bg-brand-hover transition-colors">
              {status.mailboxes.length ? <Plus className="w-4 h-4" strokeWidth={1.5} /> : <Link2 className="w-4 h-4" strokeWidth={1.5} />}
              {status.mailboxes.length ? "Aggiungi un'altra casella" : "Collega account Google"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
